import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Input, Loading } from '../../common';

interface CreateGuideDialogProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: (totalWeight: number, quantityPackages: number) => void;
  deliveryId: string;
  defaultWeight?: number;
  defaultQuantity?: number;
  loading?: boolean;
}

const CreateGuideDialog: React.FC<CreateGuideDialogProps> = ({
  visible,
  onHide,
  onConfirm,
  deliveryId,
  defaultWeight = 0,
  defaultQuantity = 0,
  loading = false
}) => {
  const [totalWeight, setTotalWeight] = useState<string>(defaultWeight > 0 ? defaultWeight.toString() : '');
  const [quantityPackages, setQuantityPackages] = useState<string>(defaultQuantity > 0 ? defaultQuantity.toString() : '');
  const [errors, setErrors] = useState<{ totalWeight?: string; quantityPackages?: string }>({});

  // Actualizar valores cuando cambien los defaults
  useEffect(() => {
    setTotalWeight(defaultWeight > 0 ? defaultWeight.toString() : '');
    setQuantityPackages(defaultQuantity ? defaultQuantity.toString() : '1');
  }, [defaultWeight, defaultQuantity]);

  // Limpiar errores cuando se oculta el dialog
  useEffect(() => {
    if (!visible) {
      setErrors({});
    }
  }, [visible]);

  const validateForm = (): boolean => {
    const newErrors: { totalWeight?: string; quantityPackages?: string } = {};

    const weightValue = parseFloat(totalWeight);
    const quantityValue = parseInt(quantityPackages);

    if (!totalWeight || isNaN(weightValue) || weightValue <= 0) {
      newErrors.totalWeight = 'El peso total debe ser mayor a cero';
    }

    if (!quantityPackages || isNaN(quantityValue) || quantityValue <= 0) {
      newErrors.quantityPackages = 'La cantidad de paquetes debe ser mayor a cero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateForm()) {
      const weightValue = parseFloat(totalWeight);
      const quantityValue = parseInt(quantityPackages);
      onConfirm(weightValue, quantityValue);
    }
  };

  const handleHide = () => {
    if (loading) return; // Prevenir cierre durante la carga
    setErrors({});
    onHide();
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTotalWeight(e.target.value);
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuantityPackages(e.target.value);
  };

  const footerContent = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={handleHide}
        className="p-button-text"
        disabled={loading}
      />
      <Button
        label={loading ? 'Creando...' : 'Crear Guía'}
        icon="pi pi-check"
        onClick={handleConfirm}
        className="p-button-success"
        loading={loading}
        disabled={loading}
      />
    </div>
  );

  return (
    <Dialog
      header="Crear Guía de Envío"
      visible={visible}
      style={{ width: '450px' }}
      onHide={handleHide}
      footer={footerContent}
      modal
      draggable={false}
      resizable={false}
      closable={!loading}
      closeOnEscape={!loading}
      dismissableMask={!loading}
    >
      {loading ? (
        <Loading text="Creando guía de envío..." />
      ) : (
        <div className="flex flex-column gap-3">
          <div>
            <label htmlFor="deliveryId" className="block text-900 font-medium mb-2">
              ID de Entrega {deliveryId}
            </label>
          </div>
          <br />

          <div>
            <Input
              type="number"
              value={totalWeight}
              onChange={handleWeightChange}
              label="Peso Total (kg)"
              id="totalWeight"
              required={true}
              description="Ingrese el peso total en kilogramos"
            />
            {errors.totalWeight && (
              <Message severity="error" text={errors.totalWeight} className="mt-1" />
            )}
          </div>

          <div>
            <Input
              type="number"
              value={quantityPackages}
              onChange={handleQuantityChange}
              label="Cantidad de Paquetes"
              id="quantityPackages"
              required={true}
              description="Ingrese la cantidad de paquetes"
            />
            {errors.quantityPackages && (
              <Message severity="error" text={errors.quantityPackages} className="mt-1" />
            )}
          </div>

        </div>
      )}
    </Dialog>
  );
};

export default CreateGuideDialog;
